import { getBetterAuthBaseUrl } from "@-/dev.improve.haniverse.lib.auth.client";
import {
  type ActorReplicaExport,
  exportActorReplica,
} from "@-/dev.improve.haniverse.lib.database.engine";
import { sendActorSyncImport } from "@-/dev.improve.haniverse.lib.database.engine/import-client";
import {
  type DeviceAccount,
  getDeviceAccount,
  setDeviceAccount,
} from "@-/dev.improve.haniverse.lib.device-settings";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { resetActorDatabaseForSignIn } from "#actor-database";
import { isActorSyncProvisioned, resolveActorSyncGatewayUrl } from "#resolve-actor-sync-url";
import type { ActorDatabaseConnection } from "#types";

/**
 * Web's replica rebuild (see the native module for the two phases). The leader worker owns the
 * OPFS file, so the change log is read through the live proxied connection; with no connection
 * there is nothing to read, and the wipe alone is the rebuild (as the sign-in reset already is).
 * Phase 2 is the sign-in wipe: the leader closes its engine under its election lock, the file is
 * removed, and the next open bootstraps from the cloud copy.
 */
export interface ActorReplicaRebuildResult {
  applied: number;
  exported: number;
  setAside: number;
  source: ActorReplicaExport["source"] | "none" | "unreadable";
}

export interface ActorReplicaRebuildPrepared {
  mark: number | null;
  result: ActorReplicaRebuildResult;
}

function signedInAccount(): DeviceAccount {
  const account = getDeviceAccount();
  if (account === null || !isActorSyncProvisioned(account)) {
    throw new Error("Only a signed-in device's replica is rebuilt");
  }
  return account;
}

function clearRebuildMarker(): void {
  const account = getDeviceAccount();
  if (account?.rebuildPending !== undefined) {
    setDeviceAccount({ ...account, rebuildPending: undefined });
  }
}

async function exportThroughConnection(
  connection: ActorDatabaseConnection,
  account: DeviceAccount,
  afterChangeId: number | undefined,
): Promise<ActorReplicaExport> {
  return exportActorReplica(
    { all: (sql) => connection.getAllAsync(sql) },
    { afterChangeId, name: `web-${account.ownerId ?? "account"}` },
  );
}

async function sendExport(
  account: DeviceAccount,
  exported: ActorReplicaExport,
  result: ActorReplicaRebuildResult,
): Promise<void> {
  setDeviceAccount({ ...(getDeviceAccount() ?? account), rebuildPending: exported.importId });
  const sent = await sendActorSyncImport({
    authToken: account.tursoDbAuthToken,
    changes: exported.changes,
    clientId: exported.importId,
    gatewayUrl: resolveActorSyncGatewayUrl(getBetterAuthBaseUrl()),
    importId: exported.importId,
    journalHead: exported.journalHead,
  });
  result.applied += sent.applied;
  result.exported += exported.changes.length;
  result.setAside += sent.setAside;
  result.source = exported.source;
}

export async function prepareActorReplicaRebuild(
  reason: string,
  connection: ActorDatabaseConnection | null,
): Promise<ActorReplicaRebuildPrepared> {
  const account = signedInAccount();
  const log = logger(import.meta.location);
  const result: ActorReplicaRebuildResult = {
    applied: 0,
    exported: 0,
    setAside: 0,
    source: "none",
  };
  if (connection === null) {
    return { mark: null, result: { ...result, source: "unreadable" } };
  }
  let exported: ActorReplicaExport;
  try {
    exported = await exportThroughConnection(connection, account, undefined);
  } catch (error) {
    log.warn("actor-db:rebuild-export-failed", { phase: 1, reason }, error);
    return { mark: null, result: { ...result, source: "unreadable" } };
  }
  await sendExport(account, exported, result);
  log.info("actor-db:rebuild-prepared", { reason, ...result });
  return { mark: exported.source === "log" ? exported.mark : null, result };
}

export async function retireActorReplicaForRebuild(
  reason: string,
  prepared: ActorReplicaRebuildPrepared,
  { close, connection }: { close: () => Promise<void>; connection: ActorDatabaseConnection | null },
): Promise<ActorReplicaRebuildResult> {
  const account = signedInAccount();
  const log = logger(import.meta.location);
  const result = { ...prepared.result };
  if (connection !== null && prepared.mark !== null) {
    // What was written while phase 1 ran, before the connection goes.
    try {
      const exported = await exportThroughConnection(connection, account, prepared.mark);
      if (exported.changes.length > 0) {
        await sendExport(account, exported, result);
      }
    } catch (error) {
      log.warn("actor-db:rebuild-export-failed", { phase: 2, reason }, error);
    }
  }
  await close();
  await resetActorDatabaseForSignIn();
  clearRebuildMarker();
  log.info("actor-db:rebuild", { reason, ...result });
  return result;
}

/** Web keeps no generation manifest: a marker with no file behind it is settled by the next rebuild. */
export function settleActorReplicaRebuildMarker(): void {
  // Nothing to inspect synchronously on OPFS; `prepareActorReplicaRebuild` handles both cases.
}
